import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Film, ArrowUp } from "lucide-react";
import { siteInfo, navLinks } from "../../data/mockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [visible, setVisible] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [honeypot, setHoneypot] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [sending, setSending] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const containsHtmlOrUrl = (text) => {
    const htmlPattern = /<[^>]*>/;
    const urlPattern = /https?:\/\/|www\.|\.com\b|\.net\b|\.org\b|\.io\b|\.co\b/i;
    return htmlPattern.test(text) || urlPattern.test(text);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setFormError("");

    // Honeypot check — bots fill hidden fields
    if (honeypot) {
      setFormSubmitted(true);
      setSending(false);
      return;
    }

    // Block HTML/URLs
    if (containsHtmlOrUrl(contactForm.message) || containsHtmlOrUrl(contactForm.name)) {
      setFormError("Links and HTML are not allowed in messages.");
      setSending(false);
      return;
    }

    try {
      await axios.post(`${API}/contact`, {
        name: contactForm.name,
        email: contactForm.email,
        message: contactForm.message,
        website: honeypot,
      });
      setFormSubmitted(true);
      setContactForm({ name: "", email: "", message: "" });
      setTimeout(() => setFormSubmitted(false), 5000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFormError(detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* Contact Section */}
      <section
        id="contact"
        ref={sectionRef}
        className="relative py-32 overflow-hidden"
        style={{ background: "#0f0f0f" }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-20">
            {/* Contact Info */}
            <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <p
                className="text-[#c8a55c] text-sm tracking-[0.3em] uppercase mb-4"
                style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600 }}
              >
                Get in Touch
              </p>
              <h2
                className="text-[#f5f0e8] text-5xl sm:text-6xl mb-8"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                Let's Create Something
              </h2>
              <p
                className="text-[#d0c8b8] text-lg leading-relaxed max-w-md"
                style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
              >
                Whether you have a project in mind or just want to connect, I'd love to hear from you. Every great story starts with a conversation.
              </p>
            </div>

            {/* Contact Form */}
            <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              {formSubmitted ? (
                <div className="h-full flex items-center justify-center p-12 border border-[#c8a55c]/20">
                  <div className="text-center">
                    <p
                      className="text-[#c8a55c] text-3xl mb-3"
                      style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}
                    >
                      Message Sent
                    </p>
                    <p
                      className="text-[#8a8070] text-sm"
                      style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                    >
                      Thank you for reaching out. I'll get back to you soon.
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot — hidden from humans, bots fill it */}
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }}>
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>
                  <div>
                    <label
                      className="text-[#b0a898] text-xs tracking-[0.15em] uppercase block mb-2"
                      style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 500 }}
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full bg-transparent border-b border-[#2a2a2a] focus:border-[#c8a55c]/50 text-[#f5f0e8] py-3 outline-none transition-colors duration-300 placeholder:text-[#5a5550]"
                      style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label
                      className="text-[#b0a898] text-xs tracking-[0.15em] uppercase block mb-2"
                      style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 500 }}
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full bg-transparent border-b border-[#2a2a2a] focus:border-[#c8a55c]/50 text-[#f5f0e8] py-3 outline-none transition-colors duration-300 placeholder:text-[#5a5550]"
                      style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label
                      className="text-[#b0a898] text-xs tracking-[0.15em] uppercase block mb-2"
                      style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 500 }}
                    >
                      Message
                    </label>
                    <textarea
                      required
                      rows={5}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="w-full bg-transparent border-b border-[#2a2a2a] focus:border-[#c8a55c]/50 text-[#f5f0e8] py-3 outline-none transition-colors duration-300 resize-none placeholder:text-[#5a5550]"
                      style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                      placeholder="Tell me about your project..."
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className={`mt-4 border px-10 py-3 transition-all duration-300 ${
                      sending
                        ? "text-[#6a6058] border-[#1f1f1f] cursor-wait"
                        : "text-[#c8a55c] border-[#c8a55c]/30 hover:bg-[#c8a55c]/10"
                    }`}
                    style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 500, fontSize: "0.85rem", letterSpacing: "0.15em" }}
                  >
                    {sending ? "SENDING..." : "SEND MESSAGE"}
                  </button>
                  {formError && (
                    <p className="mt-4 text-red-400 text-sm" style={{ fontFamily: "'Open Sans', sans-serif" }}>
                      {formError}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 border-t border-[#1a1a1a]" style={{ background: "#0a0a0a" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <Film className="w-5 h-5 text-[#c8a55c]" />
              <span
                className="text-[#f5f0e8] text-sm tracking-[0.2em] uppercase"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                {siteInfo.name}
              </span>
            </div>

            {/* Nav Links */}
            <nav className="flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="text-[#9a9088] hover:text-[#c8a55c] text-xs tracking-[0.1em] uppercase transition-colors duration-300"
                  style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 400 }}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Back to top */}
            <button
              onClick={scrollToTop}
              className="text-[#9a9088] hover:text-[#c8a55c] transition-colors duration-300 flex items-center gap-2 group"
            >
              <span
                className="text-xs tracking-[0.1em] uppercase"
                style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 400 }}
              >
                Top
              </span>
              <ArrowUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform duration-300" />
            </button>
          </div>

          {/* QR Code Share */}
          <div className="mt-10 pt-8 border-t border-[#1f1f1f] flex flex-col items-center gap-4">
            <p
              className="text-[#b0a898] text-sm tracking-wide"
              style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 400 }}
            >
              Share with a friend — have them scan here:
            </p>
            <img
              src="/images/ssf-qrcode.png"
              alt="Scan to visit stevesattlerfilms.com"
              className="w-28 h-28"
            />
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-8 border-t border-[#1f1f1f] flex flex-col items-center gap-2">
            <p
              className="text-[#7a7570] text-xs text-center tracking-wide"
              style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
            >
              Copyright &copy; 2026 stevesattlerfilms.com
            </p>
            <p
              className="text-[#7a7570] text-xs text-center tracking-wide"
              style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
            >
              Hosted and Design by{" "}
              <a
                href="https://web-techservices.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9a9088] hover:text-[#c8a55c] transition-colors duration-300"
              >
                Web-techservices.com
              </a>
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
